import React, { useState } from 'react'
import styled from '@emotion/styled'
import { bool, object, string } from 'prop-types'
import { makeImgResizerUrl } from '@jelly/utils'

import { AnchorTag } from '../AnchorTag'
import { MonetizationButton } from '../MonetizationButton'

// Styled Components via Emotion
const CardContainer = styled.div`
	border-bottom: 1px solid rgba(109, 109, 109, 0.1);
	display: flex;
	flex-direction: column;
	margin: auto;
	padding: 0 0 20px 0;
	width: 90%;

	${({ theme }) => theme.MQ.M} {
		width: 100%;
	}
`
const FeaturedSponsor = styled.div`
	color: ${({ theme }) => theme.colors.blue};
	font-size: 14px;
	padding-bottom: 20px;
	padding-top: 20px;
`
const ServiceLogoWrapper = styled.div`
	margin-bottom: 28px;
	width: 140px;

	img {
		max-width: 140px;
		width: 100%;
	}
`
const ServiceLogo = styled.img`
	display: block;
`
const ContextContainer = styled.div`
	display: block;
	width: 100%;
`
const ServiceName = styled.div`
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 16px;

	${({ as, theme }) =>
		as &&
		`
			&:hover {
			color: ${theme.navigation.linkHoverColor};
		}
	`}
`
const ContextWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 8px;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
		justify-content: space-between;
	}
`
const DescriptionText = styled.div`
	color: ${({ theme }) => theme.colors.darkGrey};
	display: flex;
	flex-direction: column;
	font-size: 14px;
	font-weight: 300;
	line-height: 20px;
	margin-bottom: 28px;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		margin-right: 70px;
		margin-bottom: 0;
		width: auto;
	}
`
const ArrowCharacter = styled.div`
	${({ theme }) => theme.triangle};
	display: inline-block;
	margin-left: 3px;
	${({ arrowUp }) => arrowUp && `transform: rotate(180deg)`};
`

const MoreLink = styled.div`
	${({ theme }) => theme.links.default};
	display: inline-block;
	font-size: 14px;
	margin-right: auto;
	text-decoration: underline;
	text-transform: uppercase;
	width: auto;

	& :hover {
		${ArrowCharacter} {
			border-bottom-color: ${({ theme }) => theme.colors.tertiary};
		}
	}

	${({ theme }) => theme.MQ.M} {
		margin-left: auto;
		margin-right: 0;
		right: 0;
	}
`
const ReviewContainer = styled.div`
	display: ${({ hideReview }) => (hideReview ? 'block' : 'none')};
	width: 100%;
`
const ButtonWrapper = styled.div`
	display: flex;
	flex-shrink: 0;
	height: fit-content;
	min-width: 200px;
`

ServiceInformationCard.propTypes = {
	affiliateLink: string,
	categoryWidgetId: string,
	descriptionText: object,
	canonical: string,
	isFeaturedBestPick: bool,
	pricing: object,
	serviceName: string,
	thumbnail: string,
}
/**
 * This Card is used for displaying information about a service
 * With an optional read more + dropdown text area
 *
 * @param {String} props.affiliateLink :: link path for service / company
 * @param {String} props.categoryWidgetId :: widget id set on the category level
 * @param {Object} props.descriptionText :: text to go in the description spot, takes in two params to display optional dropdown
 * @param {Boolean} props.isFeaturedBestPick :: flag to determine if service is marked as featured best pick service
 * @param {Object} props.pricing :: data used for monetization
 * @param {String} props.thumbnail :: url path for service logo
 */

export function ServiceInformationCard({
	affiliateLink,
	categoryWidgetId,
	descriptionText: { serviceDescription, restOfReview },
	isFeaturedBestPick,
	pricing,
	serviceName,
	thumbnail,
}) {
	const [showRestOfReview, setShowRestOfReview] = useState(false)
	const logoSrc = makeImgResizerUrl({ src: thumbnail.path, method: 'h', h: 80 })

	const handleReviewClick = () => {
		setShowRestOfReview(!showRestOfReview)
	}

	return (
		<CardContainer>
			{isFeaturedBestPick && <FeaturedSponsor>Featured Sponsor</FeaturedSponsor>}
			<ServiceLogoWrapper
				id={`${serviceName}-link-company-logo`}
				{...(affiliateLink && { alt: serviceName, as: AnchorTag, href: affiliateLink })}
			>
				<ServiceLogo className="lazyload" data-src={logoSrc} alt={`${serviceName}-logo`} />
			</ServiceLogoWrapper>
			<ContextContainer>
				<ServiceName
					id={`${serviceName}-link-company-name`}
					{...(affiliateLink && { as: AnchorTag, href: affiliateLink })}
				>
					{serviceName}
				</ServiceName>
				<ContextWrapper>
					<DescriptionText>
						<div dangerouslySetInnerHTML={{ __html: serviceDescription }} />
						{restOfReview && (
							<>
								<ReviewContainer
									hideReview={showRestOfReview}
									dangerouslySetInnerHTML={{ __html: restOfReview }}
								/>
								<MoreLink onClick={handleReviewClick}>
									{showRestOfReview ? `Less` : `More`}
									<ArrowCharacter arrowUp={!showRestOfReview} />
								</MoreLink>
							</>
						)}
					</DescriptionText>
					<ButtonWrapper>
						<MonetizationButton
							categoryWidgetId={categoryWidgetId}
							pricing={pricing}
							serviceName={serviceName}
							sectionType="ourreviews"
							showArrow={false}
							templateType="mbp"
						/>
					</ButtonWrapper>
				</ContextWrapper>
			</ContextContainer>
		</CardContainer>
	)
}
